import { styled, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import {
  listTableBodyCellStyles,
  listTableHeaderCellStyles,
} from 'components/list-table/list-table.styles'
import InvoiceDialog from '../../invoice-dialog/invoice.dialog'
import { Fragment, useMemo, useState } from 'react'
import { TransformInvoiceResponse, InvoiceType, InvoiceResponse } from '../../../../../store/invoices/invoices.types'
import { getInvoiceAgingDays, matchesInvoiceAgingFilter } from '../../../invoice.utils'
import InvoiceTableRow from '../invoice-table-row/invoice-table-row'
import { useDispatch, useSelector } from 'react-redux'
import {
  clearSelectedInvoiceId,
  clearSelectedOrderId,
} from 'store/invoices/invoices.slice'
import { RootState } from 'store'

export const StyledTableCell = styled(TableCell)({
  ...listTableHeaderCellStyles,
  ...listTableBodyCellStyles,
})

type SortColumn = 'invoice' | 'company' | 'aging' | 'amount' | null
type SortDirection = 'asc' | 'desc'

interface InvoiceTableBodyProps {
  invoices: TransformInvoiceResponse[] | undefined
  selectedInvoiceIds: Set<string>
  onInvoiceSelect: (invoiceId: string, selected: boolean) => void
  invoiceType: InvoiceType
  sortColumn: SortColumn
  sortDirection: SortDirection
  showBatchColumn?: boolean
  onBatchClick?: (batchId: number) => void
  groupByBatch?: boolean
}

const InvoiceTableBody = ({ 
  invoices, 
  selectedInvoiceIds, 
  onInvoiceSelect,
  invoiceType,
  sortColumn,
  sortDirection,
  showBatchColumn = false,
  onBatchClick,
  groupByBatch = false,
}: InvoiceTableBodyProps) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [defaultTab, setDefaultTab] = useState<number | undefined>(undefined)

  const dispatch = useDispatch()
  const { duration } = useSelector((state: RootState) => state.invoices)

  const handleDialogOpen = (tab?: number) => {
    setDefaultTab(tab)
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setDefaultTab(undefined)
    dispatch(clearSelectedOrderId())
    dispatch(clearSelectedInvoiceId())
  }

  const allRows: InvoiceResponse[] = useMemo(() => {
    if (!invoices) return []

    const rows: InvoiceResponse[] = []
    invoices.forEach((group) => {
      group.invoiceData.forEach((item) => rows.push(item))
    })

    return rows
  }, [invoices])

  const filteredRows: InvoiceResponse[] = useMemo(() => {
    return allRows.filter((row) => matchesInvoiceAgingFilter(row, invoiceType, duration))
  }, [allRows, duration, invoiceType])

  const sortedRows: InvoiceResponse[] = useMemo(() => {
    if (!sortColumn) return filteredRows

    const directionMultiplier = sortDirection === 'desc' ? -1 : 1
    const rows = [...filteredRows]

    rows.sort((a, b) => {
      let cmp = 0
      switch (sortColumn) {
        case 'invoice': {
          const aVal = a.userInvoiceId ?? ''
          const bVal = b.userInvoiceId ?? ''
          cmp = aVal.localeCompare(bVal)
          break
        }
        case 'company': {
          const aVal = a.customerName ?? ''
          const bVal = b.customerName ?? ''
          cmp = aVal.localeCompare(bVal)
          break
        }
        case 'aging': {
          const aAging = getInvoiceAgingDays(a)
          const bAging = getInvoiceAgingDays(b)
          cmp = aAging - bAging
          break
        }
        case 'amount': {
          cmp = a.totalAmount - b.totalAmount
          break
        }
        default:
          return 0
      }
      return directionMultiplier * cmp
    })
    return rows
  }, [filteredRows, sortColumn, sortDirection])

  const groupedRows = useMemo(() => {
    const groups = new Map<string, { label: string; rows: InvoiceResponse[] }>()

    sortedRows.forEach((row) => {
      const fallbackLabel = row.userInvoiceId || row.orderId || row.id
      const groupLabel = row.batchDisplayId ?? (row.batchId ? `#${row.batchId}` : fallbackLabel)
      const groupKey = row.batchId ? `batch-${row.batchId}` : `invoice-${fallbackLabel}`

      const existing = groups.get(groupKey)
      if (existing) {
        existing.rows.push(row)
        return
      }

      groups.set(groupKey, {
        label: groupLabel,
        rows: [row],
      })
    })

    return Array.from(groups.entries()).map(([key, group]) => ({
      key,
      ...group,
      totalAmount: group.rows.reduce((acc, row) => acc + row.totalAmount, 0),
      currency: group.rows[0]?.currency || 'CAD',
    }))
  }, [sortedRows])

  const groupHeaderColSpan = showBatchColumn ? 7 : 6

  return (
    <>
      <TableBody>
        {groupByBatch
          ? groupedRows.map((group) => (
              <Fragment key={group.key}>
                <TableRow key={`header-${group.key}`}>
                  <TableCell colSpan={groupHeaderColSpan} sx={{ backgroundColor: 'grey.100' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Batch {group.label} Total: {group.currency} ${group.totalAmount.toFixed(2)}
                    </Typography>
                  </TableCell>
                </TableRow>
                {group.rows.map((item) => (
                  <InvoiceTableRow
                    key={item?.id}
                    invoice={item}
                    handleViewInvoice={handleDialogOpen}
                    isSelected={selectedInvoiceIds.has(item.id)}
                    onSelect={(selected) => onInvoiceSelect(item.id, selected)}
                    invoiceType={invoiceType}
                    showBatchColumn={showBatchColumn}
                    onBatchClick={onBatchClick}
                  />
                ))}
              </Fragment>
            ))
          : sortedRows.map((item) => (
              <InvoiceTableRow
                key={item?.id}
                invoice={item}
                handleViewInvoice={handleDialogOpen}
                isSelected={selectedInvoiceIds.has(item.id)}
                onSelect={(selected) => onInvoiceSelect(item.id, selected)}
                invoiceType={invoiceType}
                showBatchColumn={showBatchColumn}
                onBatchClick={onBatchClick}
              />
            ))}
      </TableBody>
      <InvoiceDialog open={dialogOpen} handleClose={handleDialogClose} defaultTab={defaultTab} />
    </>
  )
}

export default InvoiceTableBody
